// ConnectorContextMenu — right-click-meny för connector-linjer. Renderas
// som en fristående floating panel vid cursor-pos eftersom Radix
// <ContextMenuTrigger asChild> är finicky med SVG-element; path-träff
// emittar `(connectorId, x, y)` via prop och WorkflowCanvas ritar denna
// komponent ovanpå vid positionen.

import { useEffect, useRef } from 'react';
import { Trash2, MapPin, Tag, X } from 'lucide-react';

export interface ConnectorContextMenuState {
  connectorId: string;
  x: number;
  y: number;
  /** Klick-koordinat i världsrymd — används av "Lägg till waypoint"
   *  för att veta var den nya waypointen ska placeras. */
  clickPointWorld?: { x: number; y: number };
  /** Snapshot av nuvarande waypoint-array vid menyns open-tid. */
  currentWaypoints?: Array<{ x: number; y: number }>;
}

interface Props {
  state: ConnectorContextMenuState | null;
  onClose: () => void;
  onDelete: (connectorId: string) => void;
  onAddWaypoint?: (
    connectorId: string,
    point: { x: number; y: number },
    insertIndex: number,
    currentWaypoints: Array<{ x: number; y: number }>,
  ) => void;
  onClearLabel?: (connectorId: string) => void;
  onEditLabel?: (connectorId: string) => void;
}

export function ConnectorContextMenu({
  state, onClose, onDelete, onAddWaypoint, onClearLabel, onEditLabel,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);

  // Click-outside och Esc stänger menyn.
  useEffect(() => {
    if (!state) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    // Delay add:en ett tick så den click som öppnade menyn inte omedelbart
    // stänger den igen.
    const t = window.setTimeout(() => {
      document.addEventListener('mousedown', onDoc);
      document.addEventListener('keydown', onKey);
    }, 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [state, onClose]);

  if (!state) return null;

  const item = 'flex items-center gap-2 w-full px-3 py-1.5 text-sm text-left hover:bg-accent rounded-sm';

  return (
    <div
      ref={ref}
      role="menu"
      aria-label="Connector context menu"
      className="fixed z-50 bg-popover border border-border rounded-md shadow-md p-1 min-w-[180px]"
      style={{ left: state.x, top: state.y }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {onAddWaypoint && state.clickPointWorld && state.currentWaypoints && (
        <button
          type="button"
          className={item}
          onClick={() => {
            onAddWaypoint(
              state.connectorId,
              state.clickPointWorld!,
              state.currentWaypoints!.length,
              state.currentWaypoints!,
            );
            onClose();
          }}
        >
          <MapPin className="w-4 h-4" /> Lägg till waypoint
        </button>
      )}
      {onEditLabel && (
        <button
          type="button"
          className={item}
          onClick={() => { onEditLabel(state.connectorId); onClose(); }}
        >
          <Tag className="w-4 h-4" /> Redigera label
        </button>
      )}
      {onClearLabel && (
        <button
          type="button"
          className={item}
          onClick={() => { onClearLabel(state.connectorId); onClose(); }}
        >
          <X className="w-4 h-4" /> Rensa label
        </button>
      )}
      <div className="h-px bg-border my-1" />
      <button
        type="button"
        className={`${item} text-destructive hover:bg-destructive/10`}
        onClick={() => { onDelete(state.connectorId); onClose(); }}
      >
        <Trash2 className="w-4 h-4" /> Ta bort anslutning
      </button>
    </div>
  );
}
